import { apiClient } from "@/api";
import { Project, Role } from "@/api/types";
import { ProjectTile } from "@/components/projects/ProjectTile";
import AppButton from "@/components/structure/AppButton";
import { useAuth } from "@/contexts/AuthContext";
import toast from "@/utils/toast";
import { faUser, faUserTie } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

export default function ProjectsByRole() {
  const { isAuthenticated, user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      setProjects([]); // Clear projects if not authenticated
      return;
    }

    apiClient
      .get<Project[]>("/projects/", { requireAuth: false, retryLogin: false })
      .then((data) => {
        setProjects(data);
      })
      .catch((error) => {
        console.error("Failed to fetch projects:", error);
        toast.apiError("Failed to fetch projects", error);
        setProjects([]); // Set to empty array on error
      });
  }, [isAuthenticated]);

  const facilitatorProjects = useCallback(() => {
    return projects.filter((project) =>
      project.roles?.includes(Role.Facilitator)
    );
  }, [projects]);

  const participantProjects = useCallback(() => {
    // remove projects that are already listed in facilitatorProjects
    const facilitatorProjectIds = new Set(
      facilitatorProjects().map((p) => p.id)
    );
    return projects
      .filter((project) => project.roles?.includes(Role.Participant))
      .filter((project) => facilitatorProjectIds.has(project.id) === false);
  }, [projects]);

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Welcome to ClarifAI
        </h2>
        <p className="text-gray-600 mb-6">
          Please sign in to view your projects.
        </p>
        <div className="flex justify-center">
        <AppButton onClick={ () => navigate('/login') } variant="secondary" className="ml-2 text-sm">
          <FontAwesomeIcon icon={faUser} className="mr-2" />
          Sign In
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {facilitatorProjects()?.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUserTie} className="pr-2 text-xl" />{" "}
              Facilitated Projects
            </h2>
            <p>Projects in which you're a facilitator.</p>
            <div className="grid gap-4 grid-cols-1">
              <AnimatePresence mode="popLayout">
              {facilitatorProjects()?.map((project) => (
                <motion.div layout key={`facilitated-project-${project.id}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  <ProjectTile project={project} key={project.id} />
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {participantProjects()?.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUser} className="pr-2 text-xl" />
              Participating Projects
            </h2>
            <p>Projects in which you're a participator, but not a facilitator.</p>
            <div className="grid gap-4 grid-cols-1">
              <AnimatePresence mode="popLayout">
              {participantProjects()?.map((project) => (
                <motion.div layout key={`participating-project-${project.id}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                >
                  <ProjectTile project={project} key={project.id} />
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {projects.length === 0 && (
          <div className="text-center text-gray-500">
            <p>No projects available at this time.</p>
          </div>
        )}
      </div>
    </>
  );
}
